import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { AppBar, Box, Drawer, styled } from "@mui/material";
import Seo from "../seo";
import felixAnalytics from "../../../felixAnalytics";
import { AssinaturaProvider } from "../../../providers/assinaturaProvider";
import SnackBarStore from "../../../stores/snackBarStores";
import AssinarStore from "../../app/assinatura/assine";
import FelixToolbar from "../../app/home/felixToolbar";
import MenuRotas from "../../app/home/menuRotas";
import TermoDeAceite from "../ui/termoDeAceite";
import DadosEscritorioModal from "../ui/dadosEscritorioModal";
import patternColorido from "../../../assets/pattern-colorido.svg";
import patternBranco from "../../../assets/pattern-branco.svg";

const PREFIX = "app";

const classes = {
  content: `${PREFIX}-content`,
  appBar: `${PREFIX}-appBar`,
  drawer: `${PREFIX}-drawer`,
  drawerPaper: `${PREFIX}-drawerPaper`,
  toolbar: `${PREFIX}-toolbar`,
};

const Root = styled("div")(({ theme }) => {
  const appBarHeight = theme.spacing(8);
  const drawerWidth = theme.spacing(8);
  return {
    [`& .${classes.content}`]: {
      position: "relative",
      minHeight: `calc(100vh - ${appBarHeight})`,
      backgroundColor: theme.palette.background.default,
      flexGrow: 1,
      paddingLeft: drawerWidth,
      // O padrão entra por ::before para poder ter opacidade própria. No light
      // ele é a arte colorida, desenhada contra o cinza neutro antigo: sobre o
      // papel creme o lavanda frio briga por temperatura e vira mancha. Fica
      // como TEXTURA (o DESIGN.md diz que a personalidade vem da tipografia,
      // não de ilustração). No dark a arte branca já é sutil por natureza.
      "&::before": {
        content: '""',
        position: "absolute",
        inset: 0,
        backgroundImage: `url(${
          theme.palette.mode === "light" ? patternColorido : patternBranco
        })`,
        opacity: theme.palette.mode === "light" ? 0.22 : 1,
        pointerEvents: "none",
        zIndex: 0,
      },
      // O conteúdo fica acima da textura.
      "& > *": { position: "relative", zIndex: 1 },
    },
    // Elevação por hairline, não por sombra: o AppBar se separa do conteúdo
    // por uma régua de 1px em `rule`.
    [`& .${classes.appBar}`]: {
      // O AppBar do MUI vem com color="primary", então seu conteúdo herda
      // `primary.contrastText` (creme). Com o fundo trocado para
      // `background.default`, no tema CLARO isso vira creme sobre creme e
      // some (era o caso do sino de notificações).
      color: theme.palette.text.primary,
      zIndex: theme.zIndex.drawer + 1,
      height: appBarHeight,
      backgroundColor: theme.palette.background.default,
      backgroundImage: "none",
      borderWidth: 0,
      borderBottomWidth: 1,
      borderStyle: "solid",
      borderColor: theme.palette.divider,
    },
    // O rail SEGUE o tema: superfície + hairline. Não usa `secondary` porque
    // no sistema novo `secondary` é a tinta (`ink`), que inverte entre light e
    // dark — era o que deixava o rail cinza-claro no dark.
    [`& .${classes.drawer}`]: {
      width: drawerWidth,
      flexShrink: 0,
      backgroundColor: theme.palette.background.paper,
    },
    [`& .${classes.drawerPaper}`]: {
      width: drawerWidth,
      backgroundColor: theme.palette.background.paper,
      backgroundImage: "none",
      borderRight: `1px solid ${theme.palette.divider}`,
    },
    [`& .${classes.toolbar}`]: {
      ...theme.mixins.toolbar,
    },
  };
});

const withMainLayout =
  <P extends object>(Component: React.ComponentType<P>): React.FC<P> =>
  (props) => {
    return (
      <Root>
        <Seo
          title="Felix na Bolsa"
          titleTemplate="%s · Webapp"
          description="Automatize os cálculos de IR dos seus clientes investidores com a nossa calculadora de IR sobre Renda Variável pensada para escritórios de contabilidade."
        />

        <AssinarStore>
          <AssinaturaProvider>
            <SnackBarStore>
              <DadosEscritorioModal />
              <TermoDeAceite />
              <AppBar
                position="sticky"
                elevation={0}
                variant="outlined"
                className={classes.appBar}
              >
                <FelixToolbar />
              </AppBar>
              <Drawer
                variant="permanent"
                className={classes.drawer}
                classes={{
                  paper: classes.drawerPaper,
                }}
              >
                <Box className={classes.toolbar}></Box>
                <MenuRotas />
              </Drawer>
              <main className={classes.content}>
                <ScrollToTop />
                <TrackPage />
                <TrackSignup />
                <Component {...props} />
              </main>
            </SnackBarStore>
          </AssinaturaProvider>
        </AssinarStore>
      </Root>
    );
  };

function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

function TrackPage() {
  const { pathname } = useLocation();
  useEffect(() => {
    felixAnalytics.setCurrentPage(pathname);
  }, []);
  return null;
}

function TrackSignup() {
  useEffect(() => {
    // Verificar se já disparou o evento Signup
    const jaDisparouSignup =
      typeof window !== "undefined" && localStorage.getItem("fbq_signup_sent");

    // Disparar apenas uma vez por usuário
    if (!jaDisparouSignup && typeof window !== "undefined" && window.fbq) {
      window.fbq("track", "Signup");
      localStorage.setItem("fbq_signup_sent", "true");
      console.log("Meta Pixel evento disparado: Signup");
    }
  }, []);

  return null;
}

export default withMainLayout;
